import { useState, useCallback } from "react";
import { fetchData, HTTP_STATUS, Notifications, intl } from "dmsbasereact";

/**
 * @param {number} organizationBranchPk
 * @returns {Object} Stock reconciliation state and handlers
 */
export const useStockReconciliation = (organizationBranchPk) => {
    const [loading, setLoading] = useState(false);
    const [stockItems, setStockItems] = useState([]);
    const [discrepancies, setDiscrepancies] = useState([]);
    const [adjustmentTypes, setAdjustmentTypes] = useState([]);
    const [pendingAdjustments, setPendingAdjustments] = useState([]);
    const [adjustmentHistory, setAdjustmentHistory] = useState([]);
    const [error, setError] = useState(null);

    const fetchStockItems = useCallback(async () => {
        if (!organizationBranchPk) return;

        try {
            const response = await fetchData(
                `/batch_location/institution/${organizationBranchPk}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setStockItems(response.data || []);
            }
        } catch (err) {
            console.error("Error fetching stock:", err);
        }
    }, [organizationBranchPk]);

    const fetchDiscrepancyReport = useCallback(async () => {
        if (!organizationBranchPk) return;

        try {
            const response = await fetchData(
                `/stock/reconcile/${organizationBranchPk}/report`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setDiscrepancies(response.data?.discrepancies || []);
            }
        } catch (err) {
            console.error("Error fetching discrepancy report:", err);
            // Handle training validation errors (403)
            if (err.response?.status === HTTP_STATUS.FORBIDDEN_403) {
                const errorMessage = err.response?.data?.detail || err.response?.data?.message || 
                    intl.get("TrainingRequiredForReconcile").d("Training is required to perform reconciliation operations.");
                Notifications.toast(
                    errorMessage,
                    Notifications.TYPE_ERROR
                );
            }
        }
    }, [organizationBranchPk]);

    const fetchAdjustmentTypes = useCallback(async () => {
        try {
            const response = await fetchData("/stock/adjustment/types", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setAdjustmentTypes(response.data?.types || []);
            }
        } catch (err) {
            console.error("Error fetching adjustment types:", err);
        }
    }, []);

    const fetchPendingAdjustments = useCallback(async () => {
        if (!organizationBranchPk) return;

        try {
            const response = await fetchData(
                `/stock/adjustment/pending?organization_branch_pk=${organizationBranchPk}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setPendingAdjustments(response.data?.adjustments || []);
            }
        } catch (err) {
            console.error("Error fetching pending adjustments:", err);
        }
    }, [organizationBranchPk]);

    const fetchAdjustmentHistory = useCallback(async () => {
        if (!organizationBranchPk) return;

        try {
            const response = await fetchData(
                `/stock/adjustment/history?organization_branch_pk=${organizationBranchPk}&limit=50`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setAdjustmentHistory(response.data?.adjustments || []);
            }
        } catch (err) {
            console.error("Error fetching adjustment history:", err);
        }
    }, [organizationBranchPk]);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                fetchStockItems(),
                fetchDiscrepancyReport(),
                fetchAdjustmentTypes(),
                fetchPendingAdjustments(),
                fetchAdjustmentHistory()
            ]);
        } catch (err) {
            setError(intl.get("ErrorLoadingData").d("Error loading data"));
        } finally {
            setLoading(false);
        }
    }, [
        fetchStockItems,
        fetchDiscrepancyReport,
        fetchAdjustmentTypes,
        fetchPendingAdjustments,
        fetchAdjustmentHistory
    ]);

    const createAdjustment = useCallback(async (adjustmentData) => {
        try {
            const response = await fetchData("/stock/adjustment", "POST", true, {
                organization_branch_pk: organizationBranchPk,
                ...adjustmentData
            });

            if (response.status === HTTP_STATUS.CREATED_201) {
                Notifications.toast(
                    intl.get("AdjustmentCreated").d("Adjustment request created successfully"),
                    Notifications.TYPE_SUCCESS
                );
                await fetchPendingAdjustments();
                return true;
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorCreatingAdjustment").d("Error creating adjustment"),
                    Notifications.TYPE_ERROR
                );
                return false;
            }
        } catch (err) {
            // Handle training validation errors (403)
            if (err.response?.status === HTTP_STATUS.FORBIDDEN_403) {
                const errorMessage = err.response?.data?.detail || err.response?.data?.message || 
                    intl.get("TrainingRequiredForReconcile").d("Training is required to perform reconciliation operations.");
                Notifications.toast(
                    errorMessage,
                    Notifications.TYPE_ERROR
                );
            } else {
                Notifications.toast(
                    intl.get("ErrorCreatingAdjustment").d("Error creating adjustment"),
                    Notifications.TYPE_ERROR
                );
            }
            return false;
        }
    }, [organizationBranchPk, fetchPendingAdjustments]);

    return {
        state: {
            loading,
            stockItems,
            discrepancies,
            adjustmentTypes,
            pendingAdjustments,
            adjustmentHistory,
            error
        },
        handlers: {
            loadAllData,
            fetchStockItems,
            fetchDiscrepancyReport,
            fetchPendingAdjustments,
            fetchAdjustmentHistory,
            createAdjustment
        }
    };
};
